import type { CSSProperties } from 'react'

import { AA_LARGE, AA_TEXT, ensureContrast, mixColor, onColor } from './contrast'

/**
 * The card surfaces a brand colour is painted against. Asset previews follow the app's light/dark
 * tokens, so both are resolved up front and CSS picks between them — no theme knowledge needed in
 * the components, which keeps them safe to render on the server.
 */
export const SURFACE_LIGHT = '#FFFFFF'
export const SURFACE_DARK = '#151820'

/** How much brand is mixed into a surface for a tinted plate (the logo plate, chips). */
export const TINT_WEIGHT = 0.18

/** Falls back to the system blue so a card without a brand colour still looks deliberate. */
export const DEFAULT_BRAND = '#316FFF'

/**
 * Every derivative of one customer-picked brand colour, as CSS custom properties:
 *
 * | property        | use                                                        |
 * | --------------- | ---------------------------------------------------------- |
 * | `--brand`       | the colour itself — fills, gradients, glows                 |
 * | `--brand-on`    | text and icons sitting **on** a brand fill                  |
 * | `--brand-on-soft` | the same, for the hero gradient's paler stop              |
 * | `--brand-ink`   | the brand used **as** text on the card surface              |
 * | `--brand-edge`  | the brand used as a rule, ring or control boundary          |
 *
 * `--brand-ink` and `--brand-edge` resolve per theme through the `.lc-brand` class in
 * `globals.css`, which is why `brandScope()` hands back a className along with the style.
 */
export const brandVars = (color?: string | null): CSSProperties => {
  const brand = color?.trim() || DEFAULT_BRAND
  const plateLight = mixColor(brand, SURFACE_LIGHT, TINT_WEIGHT)
  const plateDark = mixColor(brand, SURFACE_DARK, TINT_WEIGHT)

  return {
    '--brand': brand,
    '--brand-on': onColor(brand),
    '--brand-on-soft': onColor(mixColor(brand, SURFACE_LIGHT, 0.87)),
    '--brand-ink-l': ensureContrast(brand, plateLight, AA_TEXT),
    '--brand-ink-d': ensureContrast(brand, plateDark, AA_TEXT),
    '--brand-edge-l': ensureContrast(brand, SURFACE_LIGHT, AA_LARGE),
    '--brand-edge-d': ensureContrast(brand, SURFACE_DARK, AA_LARGE),
  } as CSSProperties
}

/**
 * Spread onto the element that owns a brand colour: `<div {...brandScope(card.mainColor)}>`.
 * Everything inside can then use `var(--brand)`, `var(--brand-on)`, `var(--brand-ink)` and
 * `var(--brand-edge)` without knowing the hex or the theme.
 */
export const brandScope = (color?: string | null, className?: string) => ({
  className: className ? `lc-brand ${className}` : 'lc-brand',
  style: brandVars(color),
})
